/**
 * The project state and project controller has one major functionality:
 *  # provide a common way of using the subbar
 *
 *  If you define a child state of sablono.project provide the following data if necessary:
 *      # good to have:
 *          # subBarTitle (i18n string)
 *          # category (APP_CATEGORY)
 *      # optional
 *          # backButtonTarget (ui-sref location, default is browser back)
 *          # hasBackButton (boolean)
 *          # isSubBarHidden (boolean)
 */
import _ from "lodash";

export default function (
    $rootScope,
    $scope,
    $log,
    $state,
    $window,
    $interval,
    $sbErrorPresenter,
    project, // from state resolve
    SABLONO_STATES,
    $sbSvg,
    $sbCalendarRepository,
    $sbStructure,
    $sbTemplate,
    EVENTS
) {
    "ngInject";

    ////////////////////////////////////////
    //
    //      Direct variables
    //
    ////////////////////////////////////////

    var vm = this;

    let dcwInterval = undefined;

    ////////////////////////////////////////
    //
    //      SCOPE properties
    //
    ////////////////////////////////////////

    vm.title = "";
    vm.category = "";

    vm.backShown = false;
    vm.backTarget = undefined;
    vm.barHidden = false;

    vm.goBack = goBack;
    vm.setPageToolbarOptions = setPageToolbarOptions;
    vm.clearCaches = clearCaches;
    vm.startDcwMode = startDcwMode;

    activate();

    ////////////////////////////////////////
    //
    //      WATCHER
    //
    ////////////////////////////////////////

    const unregisterOnStateChangeStart = $rootScope.$on(
        "$stateChangeStart",
        function onStateChangeStart(event, toState, toParams, fromState) {
            if (event.defaultPrevented) {
                return;
            }

            if (fromState.name !== toState.name) {
                vm.setPageToolbarOptions(toState.data);
                vm.clearCaches();
            }
        }
    );

    const unregisterOnPlanWorkReviewSectionChanged = $rootScope.$on(
        EVENTS.PLAN_WORK_REVIEW_SECTION_CHANGED,
        function onSectionChange(event, toSection) {
            var mergedOptions = _.assign({}, $state.current.data, {
                category: toSection,
            });

            vm.setPageToolbarOptions(mergedOptions);
        }
    );

    const unregisterOnDcwScreenModeChange = $rootScope.$on(
        EVENTS.DCW_SCREEN_MODE_CHANGE,
        function (evt, payload) {
            if (payload.isOn) {
                startDcwMode();
            } else {
                stopDcwMode();
            }
        }
    );

    $scope.$on("$destroy", function onDestroy() {
        unregisterOnStateChangeStart();
        unregisterOnPlanWorkReviewSectionChanged();
        unregisterOnDcwScreenModeChange();
        stopDcwMode();
    });

    ////////////////////////////////////////
    //
    //      IMPL
    //
    ////////////////////////////////////////

    function activate() {
        vm.setPageToolbarOptions($state.current.data);
        vm.clearCaches();
    }

    /**
     * Set sub-state specific toolbar options
     * like back navigation button, page title and coloring.
     *
     * @param options
     * @param options.subBarTitle {string}
     * @param options.category {string} - One of PLAN, WORK, REVIEW
     * @param options.hasBackButton {boolean}
     * @param options.backButtonTarget {String} - A state identifier
     * @param options.isSubBarHidden {boolean}
     */
    function setPageToolbarOptions(options) {
        vm.title = options.subBarTitle;
        vm.category = options.category;

        vm.backShown = !!options.hasBackButton;
        vm.backTarget = options.backButtonTarget;
        vm.barHidden = !!options.isSubBarHidden;
    }

    /**
     * Clear the runtime API cache!
     */
    function clearCaches() {
        $log.debug("API cache cleared.");

        $sbTemplate.clearCache();
        $sbCalendarRepository.clear();
    }

    function stopDcwMode() {
        if (dcwInterval) {
            $interval.cancel(dcwInterval);
            dcwInterval = undefined;
        }
    }

    function startDcwMode() {
        $sbSvg
            .fetchAllVisualizationsByProject(project.id)
            .then(function (visualizations) {
                if (visualizations.length === 0) {
                    $sbErrorPresenter.catch(
                        new Error(
                            "No visualizations found. Please create a visualization first."
                        )
                    );
                    return;
                }
                let rotationIndex = 1;
                const rotations = [
                    () =>
                        $state.go(SABLONO_STATES.visualizationSketch, {
                            id: visualizations[0].key || visualizations[0].id,
                        }),
                    () =>
                        $state.go(SABLONO_STATES.tracker, {
                            expand: 2,
                        }),
                    () => $state.go(SABLONO_STATES.dashboardPro),
                ];

                // start immediately with the first rotation
                rotations[0]();

                // start the interval
                dcwInterval = $interval(
                    () => {
                        if (rotationIndex >= rotations.length) {
                            rotationIndex = 0;
                        }
                        const nextRotation = rotations[rotationIndex];
                        rotationIndex++;
                        nextRotation();
                    },
                    30_000 // 30 seconds
                );
            })
            .catch((err) => $log.error(err));
    }

    function goBack() {
        if (_.isString(vm.backTarget)) {
            $state.go(vm.backTarget);
        } else {
            $window.history.back();
        }
    }
}
